import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import SendIcon from "@mui/icons-material/Send";
import MicIcon from "@mui/icons-material/Mic";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import Button from "../../../../../../../../components/button";
import {
  ButtonSize,
  ButtonVariant,
} from "../../../../../../../../components/button/types";
import {
  ProfessionalWaveform,
  SimpleProfessionalWaveform,
} from "./AudioWaveForm";

interface InputAreaProps {
  onSendText: (text: string) => void;
  onSendAudio: (data: { audioBlob: Blob; duration: number }) => void;
  onNextStep: () => void;
  onSkip: () => void;
  isThinking: boolean;
  canMoveToNext: boolean;
  isLastStep: boolean;
}

export const InputArea: React.FC<InputAreaProps> = ({
  onSendText,
  onSendAudio,
  onSkip,
  onNextStep,
  isThinking,
  canMoveToNext,
  isLastStep,
}) => {
  const { t } = useTranslation("cvEditor");
  const [text, setText] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [width, setWidth] = useState(200);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isLimitReached, setIsLimitReached] = useState(false);
  const handleSendText = () => {
    if (text.trim() && !isThinking) {
      onSendText(text);
      setText("");
    }
  };

  useEffect(() => {
    function updateWidth() {
      const w = window.innerWidth;
      if (w >= 1024) {
        setWidth(300); // lg breakpoint
      } else if (w >= 600) {
        setWidth(300); // md breakpoint
      } else {
        setWidth(200); // sm and below
      }
    }

    updateWidth();
    window.addEventListener("resize", updateWidth);

    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const handleStartRecording = useCallback(async () => {
    if (isThinking || !navigator.mediaDevices?.getUserMedia) {
      alert("Microphone access is not available.");
      return;
    }
    try {
      setIsLimitReached(false); // Reset the limit flag on new recording
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMediaStream(stream); // Set the stream for the waveform component
      setIsRecording(true);

      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      recorder.start();
    } catch (err) {
      console.error("Microphone access error:", err);
      alert(
        "Microphone access was denied. Please enable it in your browser settings."
      );
    }
  }, [isThinking]);

  const stopRecording = useCallback(
    (sendData: boolean) => {
      // --- START OF THE FIX ---

      // We only need to check if the recorder exists. It might have already been
      // stopped by the timer, but we still need to process the data and clean up.
      if (!mediaRecorderRef.current) {
        return;
      }

      // 1. Set up the onstop handler to process the data.
      // This is safe to do even if the recorder is already stopped.
      mediaRecorderRef.current.onstop = () => {
        if (sendData && audioChunksRef.current.length > 0) {
          const audioBlob = new Blob(audioChunksRef.current, {
            type: "audio/webm",
          });
          onSendAudio({ audioBlob, duration: recordingDuration });
        }
        // Clean up the data chunks after processing
        audioChunksRef.current = [];
      };

      // 2. If the recorder is still recording, stop it.
      // If it was already stopped by the timer, this will do nothing.
      if (mediaRecorderRef.current.state === "recording") {
        mediaRecorderRef.current.stop();
      } else if (mediaRecorderRef.current.state === "inactive") {
        // If it's already stopped, we need to manually trigger the onstop logic
        // because the event won't fire again.
        if (mediaRecorderRef.current.onstop) {
          mediaRecorderRef.current.onstop(new Event("stop"));
        }
      }

      // 3. Clean up the stream and UI state immediately.
      // This is safe because the data is already captured in audioChunksRef.
      if (mediaStream) {
        mediaStream.getTracks().forEach((track) => track.stop());
      }
      setMediaStream(null);
      setIsRecording(false); // This will reset the UI and timer

      // --- END OF THE FIX ---
    },
    [mediaStream, onSendAudio, recordingDuration]
  );

  // Timer effect - This is now correct and will not cause the "starts at 2s" bug.
  useEffect(() => {
    if (isRecording) {
      const startTime = Date.now();
      timerIntervalRef.current = setInterval(() => {
        const currentDuration = Math.floor((Date.now() - startTime) / 1000);
        setRecordingDuration(currentDuration);

        if (currentDuration >= 120) {
          //TODO: get the limit from the backend
          // 1. Set the flag to true to change the UI
          setIsLimitReached(true);

          // 2. Stop the recorder, but DON'T send the data automatically.
          // The user will decide.
          if (mediaRecorderRef.current?.state === "recording") {
            mediaRecorderRef.current.stop();
          }

          // 3. Stop the timer itself.
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
          }
        }
        // --- END OF UPDATE ---
      }, 1000);
    } else {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
      setRecordingDuration(0);
    }

    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    };
  }, [isRecording]);

  const formatDuration = (totalSeconds: number) => {
    const minutes = Math.floor(totalSeconds / 60)
      .toString()
      .padStart(2, "0");
    const seconds = (totalSeconds % 60).toString().padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  if (isRecording) {
    return (
      <div className="flex-shrink-0 p-3 bg-white border-t border-gray-200">
        <div
          className={`flex items-center justify-between rounded-full p-1 transition-colors ${
            isLimitReached ? "bg-red-100" : "bg-gray-100"
          }`}
        >
          <button
            onClick={() => stopRecording(false)}
            disabled={isThinking}
            className="p-2 text-white-800 hover:bg-red-50 rounded-full"
          >
            <CancelRoundedIcon />
          </button>
          <div className="flex-grow mx-2 h-10 flex items-center justify-center">
            {isLimitReached ? (
              <p className="text-xs font-semibold text-red-600 animate-pulse">
                {t(
                  "interview.recordingLimitReached",
                  "Recording limit reached"
                )}
              </p>
            ) : (
              <ProfessionalWaveform
                stream={mediaStream}
                width={width}
                height={70}
                variant="symmetric"
                barCount={50}
                sensitivity={1.0}
                smoothing={0.4}
                showPeakIndicators={true}
              />
            )}
          </div>
          <p
            className={`text-sm w-14 text-center ${
              isLimitReached ? "text-red-600 font-bold" : "text-gray-600"
            }`}
          >
            {formatDuration(recordingDuration)}
          </p>
          <button
            onClick={() => stopRecording(true)}
            disabled={isThinking}
            className="p-2 text-Yellow-default hover:bg-yellow-800 rounded-full"
          >
            <CheckCircleOutlineRoundedIcon />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 bg-white border-t border-gray-200 space-y-2">
      <div className="flex items-center gap-2">
        <input
          type="text"
          className="flex-grow w-full px-4 py-2 text-gray-700 bg-gray-100 border border-transparent rounded-full focus:outline-none focus:ring-2 focus:ring-yellow-400"
          placeholder={t("interview.inputPlaceholder") || "missing static"}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && handleSendText()}
          disabled={isThinking}
        />
        <button
          onClick={text.trim() ? handleSendText : handleStartRecording}
          disabled={isThinking}
          className="p-2 text-white bg-yellow-400 rounded-full shadow-md hover:bg-yellow-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:bg-gray-300"
        >
          {text.trim() ? (
            <SendIcon style={{ color: "#ffffff" }} />
          ) : (
            <MicIcon style={{ color: "#ffffff" }} />
          )}
        </button>
      </div>
      <div className="flex justify-between items-center pt-1 text-sm gap-2">
        <Button
          variant={ButtonVariant.SECONDARY}
          size={ButtonSize.SMALL}
          onClick={onSkip}
          disabled={isThinking}
          className="w-full"
        >
          {t("interview.skipButton")}
        </Button>

        <Button
          variant={ButtonVariant.PRIMARY}
          size={ButtonSize.SMALL}
          onClick={onNextStep}
          disabled={isThinking || !canMoveToNext}
          isLoading={isThinking}
          className="w-full"
        >
          {isLastStep
            ? t("interview.finishButton")
            : t("interview.nextPositionButton")}
        </Button>
      </div>
    </div>
  );
};
